import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import * as billing from '@/services/billing';
import { BILLING_TENANT_STORAGE_KEY } from '@/services/billingTenantContext';
import { listTenantsAdmin, type TenantDto } from '@/services/tenants';
import { getDashboardOrigin } from '@/utils/hostOrigins';

const dashboardLoginUrl = () => `${getDashboardOrigin()}/login`;

const formatMinorUnits = (cents: number, currency: string) =>
  `${(Number(cents) / 100).toFixed(2)} ${currency}`;

const BillingConsolePage: React.FC = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const role = user?.role?.toLowerCase() ?? '';

  const [credits, setCredits] = useState<any[] | null>(null);
  const [plans, setPlans] = useState<any[] | null>(null);
  const [payg, setPayg] = useState<any | null>(null);
  const [adminPlans, setAdminPlans] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [planSlug, setPlanSlug] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [checkoutTeachers, setCheckoutTeachers] = useState<
    { teacherAuthUserId: string; displayName: string; currencyCode: string; unitAmountCents: number }[]
  >([]);
  const [quantity, setQuantity] = useState(4);
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [unitCents, setUnitCents] = useState(2500);
  const [lemonVariant, setLemonVariant] = useState('');

  /** Lesson-pricing target: self for teachers; superadmin picks from tenant. */
  const [pricingTeachers, setPricingTeachers] = useState<{ authUserId: string; displayName: string }[]>([]);
  const [pricingTeacherId, setPricingTeacherId] = useState('');

  const [adminTenants, setAdminTenants] = useState<TenantDto[]>([]);
  const [superadminTenantId, setSuperadminTenantId] = useState<string | null>(null);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);

  const [lemonPlatformUrls, setLemonPlatformUrls] = useState<{
    loaded: boolean;
    customerPortalUrl?: string;
    updatePaymentMethodUrl?: string;
    needsSubscription?: boolean;
  }>({ loaded: false });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    if (role !== 'superadmin') {
      setTenantsLoaded(true);
      setAdminTenants([]);
      setSuperadminTenantId(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const list = await listTenantsAdmin();
        if (cancelled) return;
        setAdminTenants(list);
        const stored = sessionStorage.getItem(BILLING_TENANT_STORAGE_KEY);
        const validStored = stored && list.some((t) => t.id === stored);
        const initial = validStored ? stored! : list[0]?.id ?? null;
        setSuperadminTenantId(initial);
        if (initial) {
          sessionStorage.setItem(BILLING_TENANT_STORAGE_KEY, initial);
        } else {
          sessionStorage.removeItem(BILLING_TENANT_STORAGE_KEY);
        }
      } catch {
        if (!cancelled) {
          setAdminTenants([]);
          setSuperadminTenantId(null);
          sessionStorage.removeItem(BILLING_TENANT_STORAGE_KEY);
        }
      } finally {
        if (!cancelled) setTenantsLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, role]);

  useEffect(() => {
    if (!isAuthenticated || !user || role !== 'student') {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await billing.getStudentCheckoutTeachers();
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : [];
        const rows = raw
          .map((t: Record<string, unknown>) => ({
            teacherAuthUserId: String(t.teacherAuthUserId ?? t.TeacherAuthUserId ?? ''),
            displayName: String(t.displayName ?? t.DisplayName ?? ''),
            currencyCode: String(t.currencyCode ?? t.CurrencyCode ?? 'EUR'),
            unitAmountCents: Number(t.unitAmountCents ?? t.UnitAmountCents ?? 0),
          }))
          .filter((t) => t.teacherAuthUserId.length > 0);
        setCheckoutTeachers(rows);
        setTeacherId((prev) => (rows.some((r) => r.teacherAuthUserId === prev) ? prev : rows[0]?.teacherAuthUserId ?? ''));
      } catch {
        if (!cancelled) {
          setCheckoutTeachers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, role]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    if (role === 'superadmin' && !tenantsLoaded) {
      return;
    }

    let cancelled = false;
    (async () => {
      setError(null);
      try {
        if (role === 'student') {
          const { data } = await billing.getStudentLessonCredits();
          if (!cancelled) setCredits(data ?? []);
        }
        if (role === 'teacher' || (role === 'superadmin' && superadminTenantId)) {
          setLemonPlatformUrls({ loaded: false });
          try {
            const { data } = await billing.getTeacherPlatformPlans();
            if (!cancelled) setPlans(data ?? []);
          } catch {
            if (!cancelled) setPlans([]);
          }
          try {
            const { data } = await billing.getPaygUsagePreview();
            if (!cancelled) {
              setPayg(data ?? null);
            }
          } catch (e: any) {
            if (!cancelled) {
              setPayg(null);
              setError(
                e.response?.data?.error ||
                  'Could not load PAYG preview (pick a tenant or check API access).'
              );
            }
          }
          try {
            const { data: links } = await billing.getTeacherPlatformCustomerLinks();
            if (!cancelled) {
              setLemonPlatformUrls({
                loaded: true,
                customerPortalUrl: links?.customerPortalUrl ?? undefined,
                updatePaymentMethodUrl: links?.updatePaymentMethodUrl ?? undefined,
              });
            }
          } catch (e: any) {
            if (!cancelled) {
              if (e.response?.status === 404) {
                setLemonPlatformUrls({ loaded: true, needsSubscription: true });
              } else {
                setLemonPlatformUrls({ loaded: true });
              }
            }
          }
        }
        if (role === 'superadmin') {
          try {
            const { data } = await billing.listSaasPlansAdmin({ activeOnly: false });
            if (!cancelled) setAdminPlans(data ?? []);
          } catch {
            if (!cancelled) setAdminPlans(null);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.error || 'Failed to load billing data');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, role, tenantsLoaded, superadminTenantId]);

  useEffect(() => {
    if (role !== 'teacher' || !user?.id) {
      return;
    }
    const label =
      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.id;
    setPricingTeacherId(user.id);
    setPricingTeachers([{ authUserId: user.id, displayName: label }]);
  }, [role, user]);

  useEffect(() => {
    if (role !== 'superadmin' || !tenantsLoaded) {
      return;
    }
    if (!superadminTenantId) {
      setPricingTeachers([]);
      setPricingTeacherId('');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await billing.getTenantTeachersForBilling();
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : [];
        const rows = raw
          .map((t: Record<string, unknown>) => ({
            authUserId: String(t.authUserId ?? t.AuthUserId ?? ''),
            displayName: String(t.displayName ?? t.DisplayName ?? t.authUserId ?? t.AuthUserId ?? ''),
          }))
          .filter((t) => t.authUserId.length > 0);
        setPricingTeachers(rows);
        setPricingTeacherId((prev) => (rows.some((r) => r.authUserId === prev) ? prev : rows[0]?.authUserId ?? ''));
      } catch {
        if (!cancelled) {
          setPricingTeachers([]);
          setPricingTeacherId('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role, superadminTenantId, tenantsLoaded]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    if (role !== 'teacher' && role !== 'superadmin') {
      return;
    }
    if (!pricingTeacherId) {
      return;
    }
    if (role === 'superadmin' && (!superadminTenantId || !tenantsLoaded)) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const params = role === 'superadmin' ? { teacherAuthUserId: pricingTeacherId } : {};
        const { data: pricing } = await billing.getTeacherLessonPricing(params);
        if (cancelled || !pricing) return;
        setCurrencyCode(pricing.currencyCode || 'EUR');
        setUnitCents(pricing.unitAmountCents ?? 2500);
        setLemonVariant(pricing.lemonVariantId ? String(pricing.lemonVariantId) : '');
      } catch {
        if (!cancelled) {
          setCurrencyCode('EUR');
          setUnitCents(2500);
          setLemonVariant('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    user,
    role,
    pricingTeacherId,
    superadminTenantId,
    tenantsLoaded,
  ]);

  const startTeacherCheckout = async () => {
    setBusy(true);
    setOkMsg(null);
    setError(null);
    try {
      const { data } = await billing.postTeacherCheckout({
        planSlug: planSlug || undefined,
        redirectUrl: window.location.origin + '/billing',
      });
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError('No checkout URL returned');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Checkout failed');
    } finally {
      setBusy(false);
    }
  };

  const startStudentCheckout = async () => {
    setBusy(true);
    setOkMsg(null);
    setError(null);
    try {
      const { data } = await billing.postStudentLessonCheckout({
        teacherAuthUserId: teacherId,
        quantity,
        redirectUrl: window.location.origin + '/billing',
      });
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError('No checkout URL returned');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Checkout failed');
    } finally {
      setBusy(false);
    }
  };

  const savePricing = async () => {
    setBusy(true);
    setOkMsg(null);
    setError(null);
    try {
      await billing.putTeacherLessonPricing(
        {
          currencyCode,
          unitAmountCents: Number(unitCents),
          lemonVariantId: lemonVariant || null,
          isActive: true,
        },
        role === 'superadmin' && pricingTeacherId ? { teacherAuthUserId: pricingTeacherId } : undefined
      );
      setOkMsg('Lesson pricing saved.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16 text-center text-slate-600">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="container mx-auto max-w-lg px-4 py-16 text-center space-y-4">
        <h1 className="text-2xl font-semibold text-slate-900">Billing</h1>
        <p className="text-gray-400">Sign in to manage subscriptions and lesson credits.</p>
        <Button asChild className="bg-violet-600 hover:bg-violet-500">
          <a href={dashboardLoginUrl()}>Sign in</a>
        </Button>
        <p className="text-xs text-gray-500">
          Or return <Link to="/" className="text-violet-400 underline">home</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-12 space-y-10">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Billing</h1>
        <p className="text-gray-400 text-sm mt-1 capitalize">
          Signed in as {user.role} — payments run on Lemon Squeezy; secrets stay on the server.
        </p>
      </div>

      {role === 'superadmin' && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
          <h2 className="text-sm font-semibold text-amber-950">Tenant context</h2>
          <p className="text-xs text-amber-900/80">
            Platform billing, PAYG preview, and lesson pricing are scoped per tenant. Choose a tenant;{' '}
            <code className="text-amber-800">X-Tenant-Id</code> is sent on billing API requests only.
          </p>
          {!tenantsLoaded ? (
            <p className="text-xs text-gray-400">Loading tenants…</p>
          ) : adminTenants.length === 0 ? (
            <p className="text-xs text-red-200">No tenants found (superadmin list failed or database empty).</p>
          ) : (
            <select
              className="w-full max-w-md bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
              value={superadminTenantId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSuperadminTenantId(v || null);
                setPricingTeacherId('');
                if (v) {
                  sessionStorage.setItem(BILLING_TENANT_STORAGE_KEY, v);
                } else {
                  sessionStorage.removeItem(BILLING_TENANT_STORAGE_KEY);
                }
              }}
            >
              {adminTenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id}
                </option>
              ))}
            </select>
          )}
        </section>
      )}

      {error ? (
        <div className="rounded-lg border border-red-500/40 bg-red-950/40 text-red-200 text-sm px-4 py-3">{error}</div>
      ) : null}
      {okMsg ? (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/40 text-emerald-200 text-sm px-4 py-3">
          {okMsg}
        </div>
      ) : null}

      {role === 'student' && credits && (
        <section className="rounded-xl border border-slate-200/90 bg-white/85 shadow-sm backdrop-blur-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Lesson credits</h2>
          {credits.length === 0 ? (
            <p className="text-gray-400 text-sm">No subscriptions yet.</p>
          ) : (
            <ul className="space-y-2 text-sm text-slate-700">
              {credits.map((r) => (
                <li key={r.subscriptionId} className="flex justify-between gap-4 border-b border-slate-200 pb-2">
                  <span>{r.teacherDisplayName || r.teacherAuthUserId}</span>
                  <span className="tabular-nums">{r.lessonCreditsBalance} credits · {r.status}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-4 border-t border-slate-200 space-y-3">
            <h3 className="text-slate-900 font-medium">Buy more lessons</h3>
            {checkoutTeachers.length > 0 ? (
              <>
                <label className="text-xs text-gray-400">Teacher</label>
                <select
                  className="w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                  value={teacherId}
                  onChange={(e) => setTeacherId(e.target.value)}
                >
                  {checkoutTeachers.map((t) => (
                    <option key={t.teacherAuthUserId} value={t.teacherAuthUserId}>
                      {t.displayName} — {formatMinorUnits(t.unitAmountCents, t.currencyCode)} / lesson
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <p className="text-xs text-amber-200/80">
                No teachers in your school have lesson pricing wired to Lemon yet. Ask your teacher or admin to set currency,
                price, and variant id on billing, or use manual ID below.
              </p>
            )}
            {teacherId && checkoutTeachers.find((t) => t.teacherAuthUserId === teacherId) ? (
              <p className="text-xs text-gray-500">
                Checkout uses the teacher&apos;s configured unit price × quantity (Lemon MoR).
              </p>
            ) : null}
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer text-violet-400/90 hover:text-violet-300">Enter teacher user ID manually</summary>
              <input
                className="mt-2 w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                placeholder="Teacher UUID (advanced)"
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
              />
            </details>
            <label className="text-xs text-gray-400">Lesson pack size (quantity)</label>
            <input
              type="number"
              min={1}
              className="w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
            />
            <Button disabled={busy || !teacherId} onClick={startStudentCheckout} className="bg-violet-600 hover:bg-violet-500">
              Continue to checkout
            </Button>
          </div>
        </section>
      )}

      {(role === 'teacher' || (role === 'superadmin' && !!superadminTenantId)) && (
        <>
          <section className="rounded-xl border border-slate-200/90 bg-white/85 shadow-sm backdrop-blur-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Platform subscription</h2>
            {lemonPlatformUrls.loaded &&
              (lemonPlatformUrls.customerPortalUrl || lemonPlatformUrls.updatePaymentMethodUrl) && (
                <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-3 space-y-2">
                  <p className="text-xs text-violet-900 font-medium">Manage billing on Lemon</p>
                  <p className="text-xs text-slate-600">
                    Update your payment method (PAYG, renewals, long-term plans) and open the customer portal — card data stays with Lemon.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {lemonPlatformUrls.updatePaymentMethodUrl ? (
                      <Button asChild variant="outline" className="border-slate-300 text-slate-800 text-sm h-9">
                        <a href={lemonPlatformUrls.updatePaymentMethodUrl} target="_blank" rel="noreferrer">
                          Update payment method
                        </a>
                      </Button>
                    ) : null}
                    {lemonPlatformUrls.customerPortalUrl ? (
                      <Button asChild variant="outline" className="border-slate-300 text-slate-800 text-sm h-9">
                        <a href={lemonPlatformUrls.customerPortalUrl} target="_blank" rel="noreferrer">
                          Customer portal
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}
            {lemonPlatformUrls.loaded && lemonPlatformUrls.needsSubscription ? (
              <p className="text-xs text-gray-500">
                After you subscribe via checkout below, links to update your card and open Lemon billing will appear here.
              </p>
            ) : null}
            {plans && plans.length > 0 ? (
              <div className="space-y-2">
                <label className="text-xs text-gray-400">Plan slug</label>
                <select
                  className="w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                  value={planSlug}
                  onChange={(e) => setPlanSlug(e.target.value)}
                >
                  <option value="">Select…</option>
                  {plans.map((p: any) => (
                    <option key={p.id} value={p.slug}>
                      {p.name} ({p.slug}) — {p.billingType}
                    </option>
                  ))}
                </select>
                <Button
                  disabled={busy || !planSlug}
                  onClick={startTeacherCheckout}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  Subscribe / manage on Lemon
                </Button>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No active platform plans configured yet.</p>
            )}
          </section>

          {payg && (
            <section className="rounded-xl border border-slate-200/90 bg-white/85 shadow-sm backdrop-blur-sm p-6 space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">PAYG usage preview</h2>
              <p className="text-xs text-gray-400">
                Meter: <span className="text-slate-800">{payg.usage?.meter}</span> —{' '}
                {payg.usage?.activeStudents} students, {payg.usage?.teachers} teachers,{' '}
                {payg.usage?.studentLessonSubscriptionsActive} active lesson subs (tenant).
              </p>
              {payg.platformSubscription && (
                <p className="text-xs text-gray-400">
                  Platform status: <span className="text-slate-800">{payg.platformSubscription.status}</span>
                  {payg.linkedPlan && (
                    <>
                      {' '}
                      · Plan <span className="text-slate-800">{payg.linkedPlan.slug}</span> (
                      {payg.linkedPlan.billingType})
                    </>
                  )}
                </p>
              )}
              <p className="text-xs text-gray-500 leading-relaxed">{payg.lemonPayg?.summary}</p>
              <a
                href={payg.lemonPayg?.usageBasedGuideUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-violet-400 underline"
              >
                Lemon: usage-based subscriptions
              </a>
            </section>
          )}

          <section className="rounded-xl border border-slate-200/90 bg-white/85 shadow-sm backdrop-blur-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Student lesson pricing (your tenant)</h2>
            <p className="text-xs text-gray-500">
              Students use your price with a Lemon variant from <strong className="text-slate-600">your store</strong> (create the product/variant in
              Lemon, then paste the numeric variant id here). See billing checklist for catalog patterns.
            </p>
            {role === 'superadmin' && (
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Teacher</label>
                <select
                  className="w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                  value={pricingTeacherId}
                  onChange={(e) => setPricingTeacherId(e.target.value)}
                >
                  {pricingTeachers.length === 0 ? (
                    <option value="">No teachers in this tenant</option>
                  ) : (
                    pricingTeachers.map((t) => (
                      <option key={t.authUserId} value={t.authUserId}>
                        {t.displayName} ({t.authUserId.slice(0, 8)}…)
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input
                className="bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                placeholder="Currency (EUR)"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
              />
              <input
                type="number"
                className="bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
                placeholder="Unit cents"
                value={unitCents}
                onChange={(e) => setUnitCents(Number(e.target.value))}
              />
            </div>
            <input
              className="w-full bg-white border border-slate-200 px-3 py-2 text-slate-900 text-sm"
              placeholder="Lemon variant id (numeric)"
              value={lemonVariant}
              onChange={(e) => setLemonVariant(e.target.value)}
            />
            <Button
              disabled={busy || (role === 'superadmin' && !pricingTeacherId)}
              onClick={savePricing}
              variant="outline"
              className="border-slate-300 text-slate-800"
            >
              Save pricing
            </Button>
          </section>
        </>
      )}

      {role === 'superadmin' && adminPlans && (
        <section className="rounded-xl border border-slate-200/90 bg-white/85 shadow-sm backdrop-blur-sm p-6 space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">All SaaS plans (superadmin)</h2>
          <ul className="text-xs text-slate-600 space-y-1 max-h-48 overflow-auto">
            {adminPlans.map((p: any) => (
              <li key={p.id}>
                {p.isActive ? '●' : '○'} {p.slug} — {p.name} ({p.billingType})
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            Full CRUD lives in the API at <code className="text-gray-400">/api/billing/saas-plans</code>.
          </p>
        </section>
      )}
    </div>
  );
};

export default BillingConsolePage;
